/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  CircularProgress,
  TablePagination,
  Chip,
  Stack,
  Tooltip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import * as XLSX from "xlsx";
import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  useBusinessSummary,
  BusinessSummaryProvider,
} from "../../contexts/albina/BusinessSummaryContext";
import dbData from "../../assets/db.json";
import STORE_NAMES from "../../utils/STORE_NAMES";
// at the top
import ExcelJS from "exceljs";
import { saveAs } from "file-saver"; // npm i file-saver if not installed

// ---------------- styles ----------------
const floatIn = keyframes`
  0% { transform: translateY(8px); opacity: 0; }
  60% { transform: translateY(-6px); opacity: 1; }
  100% { transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Root = styled("div")(({ theme }) => ({
  minHeight: "100%",
  padding: theme.spacing(3),
  background:
    "radial-gradient(circle at 10% 10%, rgba(6,182,212,0.06), transparent 10%), radial-gradient(circle at 90% 90%, rgba(99,102,241,0.04), transparent 10%), linear-gradient(180deg, rgba(246,248,255,0.6), rgba(250,250,255,0.94))",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Title = styled("h2")(({ theme }) => ({
  margin: 0,
  fontSize: "1.6rem",
  letterSpacing: "-0.2px",
  background: "linear-gradient(90deg, #06b6d4, #6366f1)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundSize: "200% 100%",
  animation: `${shimmer} 3s linear infinite`,
  fontWeight: 700,
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.8), rgba(250,250,255,0.9))",
  boxShadow: "0 8px 30px rgba(2,6,23,0.04)",
  padding: theme.spacing(2),
}));

// ---------------- helpers ----------------
const parseDynamicKey = (key) => {
  if (key.startsWith("sd_")) {
    let tag = "";
    if (key.includes("_t_")) tag = "Take Away";
    else if (key.includes("_d_")) tag = "Dine In";
    else if (key.includes("_w_")) tag = "Walk In";
    return { type: "Net Sales", tag, key };
  }
  if (key.startsWith("pt_")) return { type: "Payment Type", tag: key, key };
  if (key.startsWith("dd_")) return { type: "Order Discount", tag: key, key };
  if (key.startsWith("iv_")) return { type: "Item Discount", tag: key, key };

  if (key.startsWith("hv_")) return { type: "Tips", tag: key, key };
  return { type: "Other", tag: "", key };
};

// Map numeric shorthand (2,3,...) to human names the user provided
const NUMERIC_STORE_NAMES = {
  3: "BOON COFFEE ROASTERS CAFE-FALAK",
  2: "BOON COFFEE ROASTERS CAFE LLC - JUMEIRAH PARK",
  1: "BOON COFFEE ROASTERS CAFE - PADEL CLUB",
  4: "BOON COFFEE ROASTERS FZCO - GOLDEN MILE",
  5: "BOON COFFEE ROASTERS CAFÉ LLC - MEADOWS VILLAGE",
  6: "BOON COFFEE ROASTERS CAFÉ LLC (BRANCH) - FORTE TOWER",
  7: "BOON PRODUCTION KITCHEN",
  8: "Dubai Hills",
};

// Try to extract a store key from a dynamic column key
// returns either a numeric string ("2"), a long id (24 hex chars), or null
// --- REPLACE existing extractStoreKeyFromKey with this ---
const extractStoreKeyFromKey = (key) => {
  const parts = key.split("_");
  // Find numeric segment
  const numeric = parts.find((p) => /^\d+$/.test(p));
  if (numeric) return numeric;
  // Find 24-hex Mongo-like id
  // const mongo = parts.find((p) => /^[a-fA-F0-9]{24}$/.test(p));
  // if (mongo) return mongo;
  // // Some keys might use other positions; as fallback try parts[2]
  // if (parts[2]) {
  //   // if it's alphanumeric > 2 chars, return as-is (could be an id)
  //   if (parts[2].length > 1) return parts[2];
  // }
  return null;
};

const mapStoreKeyToName = (key) => {
  if (!key) return "Unknown Store";
  if (NUMERIC_STORE_NAMES[key]) return NUMERIC_STORE_NAMES[key];
  if (STORE_NAMES[key]) return STORE_NAMES[key];
  return `Store ${key}`;
};

// ---------------- composant principal ----------------
const BusinessSummary = () => {
  const {
    businessSummary,
    loading,
    error,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
  } = useBusinessSummary();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Advanced search states
  const [netSalesMin, setNetSalesMin] = React.useState("");
  const [netSalesMax, setNetSalesMax] = React.useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = React.useState("");
  const [storeFilter, setStoreFilter] = React.useState("");

  // Payment types table states
  const [pagePT, setPagePT] = React.useState(0);
  const [rowsPerPagePT, setRowsPerPagePT] = React.useState(10);
  const [paymentNameFilter, setPaymentNameFilter] = React.useState("");
  const [dateFromPT, setDateFromPT] = React.useState("");
  const [dateToPT, setDateToPT] = React.useState("");

  const normalizeControlDates = (control = []) =>
    control
      .map((raw) => {
        // "Hours: 2026-01-16 06:00:00"
        const clean = raw.replace(/^Hours:\s*/, "");
        const d = new Date(clean);
        return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10); // YYYY-MM-DD
      })
      .filter(Boolean);
  // ---------------- dynamic columns ----------------
  const dynamicColumns = useMemo(() => {
    if (!businessSummary) return [];
    const keys = new Set();
    businessSummary.forEach((item) => {
      (item.data || []).forEach((d) =>
        Object.keys(d).forEach((k) => {
          if (
            k.startsWith("sd_") ||
            k.startsWith("pt_") ||
            k.startsWith("dd_") ||
            k.startsWith("hv_") ||
            k.startsWith("iv_")
          )
            keys.add(k);
        })
      );
    });
    return Array.from(keys).map(parseDynamicKey);
  }, [businessSummary]);

  // ---------------- format data ----------------
  // Build rows and a per-store breakdown so we can later filter properly
  const formattedData = useMemo(() => {
    if (!businessSummary) return [];
    const rows = [];
    // ---------- add near the top of the file (after imports) ----------

    businessSummary.forEach((item) => {
      const dataArray = item.data || [];
      const controlDates = normalizeControlDates(
        item?.control_structure?.added || []
      );

      dataArray.forEach((d, index) => {
        const groupedAll = {
          "Net Sales": 0,
          Tax: 0,
          "Payment Type": {},
          "Order Discount": 0,
          "Item Discount": 0,
          Tips: 0,
        };

        // per-store breakdown: { storeKey: { 'Net Sales':..., 'Payment Type': {...}, ... } }
        const perStore = {};

        dynamicColumns.forEach((col) => {
          if (d[col.key] == null) return;

          // get the value
          const value = d[col.key];

          // try to determine store key
          const storeKey = extractStoreKeyFromKey(col.key);

          // helper to ensure store object exists
          const ensureStore = (k) => {
            if (!perStore[k])
              perStore[k] = {
                "Net Sales": 0,
                Tax: 0,
                "Payment Type": {},
                "Order Discount": 0,
                "Item Discount": 0,

                Tips: 0,
              };
            return perStore[k];
          };

          if (col.type === "Payment Type") {
            const paymentId = col.key.split("_").pop();
            const match = dbData.find((db) => db._id === paymentId);
            const name = match ? match.name : paymentId;
            if (storeKey) {
              const s = ensureStore(storeKey);
              s["Payment Type"][name] = (s["Payment Type"][name] || 0) + value;
            } else {
              groupedAll["Payment Type"][name] =
                (groupedAll["Payment Type"][name] || 0) + value;
            }
          } else if (col.type === "Net Sales") {
            if (storeKey) {
              const s = ensureStore(storeKey);
              s["Net Sales"] += value;
            } else groupedAll["Net Sales"] += value;
          } else if (col.type === "Order Discount") {
            if (storeKey) {
              const s = ensureStore(storeKey);
              s["Order Discount"] += value;
            } else groupedAll["Order Discount"] += value;
          } else if (col.type === "Item Discount") {
            if (storeKey) {
              const s = ensureStore(storeKey);
              s["Item Discount"] += value;
            } else groupedAll["Item Discount"] += value;
          } else if (col.type === "Tips") {
            if (storeKey) {
              const s = ensureStore(storeKey);
              s["Tips"] += value;
            } else groupedAll["Tips"] += value;
          }
        });

        // calculate tax for each store
        Object.values(perStore).forEach((s) => {
          s["Tax"] = Math.round(s["Net Sales"] * 0.05);
        });

        // totals (all stores combined)
        // sum perStore into groupedAll so groupedAll represents the full row totals still
        Object.values(perStore).forEach((s) => {
          groupedAll["Net Sales"] += s["Net Sales"] || 0;
          groupedAll["Tax"] += s["Tax"] || 0;
          groupedAll["Order Discount"] += s["Order Discount"] || 0;
          groupedAll["Item Discount"] += s["Item Discount"] || 0;
          groupedAll["Tips"] += s["Tips"] || 0;
          Object.entries(s["Payment Type"] || {}).forEach(([n, v]) => {
            groupedAll["Payment Type"][n] =
              (groupedAll["Payment Type"][n] || 0) + v;
          });
        });

        // calculate tax for groupedAll
        groupedAll["Tax"] = Math.round(groupedAll["Net Sales"] * 0.05);

        const addedDate = controlDates[index] ?? null;

        const storeIds = Object.keys(perStore);

        const row = {
          addedDate,
          addedDateTs: addedDate ? new Date(addedDate).getTime() : null,

          iv: d.iv ?? "N/A",
          sd: d.sd ?? "N/A",
          hv: d.hv ?? "N/A",
          dd: d.dd ?? "N/A",
          storeIds,
          perStore,
          grouped: { ...groupedAll }, // overall grouped totals
        };
        rows.push(row);
      });
    });

    return rows;
  }, [businessSummary, dynamicColumns]);

  // ---------------- unique payment types ----------------
  const uniquePaymentTypes = useMemo(() => {
    const types = new Set();
    formattedData.forEach((row) => {
      const pt = row.grouped["Payment Type"];
      if (pt) {
        Object.keys(pt).forEach((name) => types.add(name));
      }
      // also add per-store payment types
      Object.values(row.perStore || {}).forEach((s) => {
        Object.keys(s["Payment Type"] || {}).forEach((name) => types.add(name));
      });
    });
    return Array.from(types);
  }, [formattedData]);

  // ---------------- unique stores ----------------
  const uniqueStores = useMemo(() => {
    const stores = new Set();
    formattedData.forEach((row) => {
      Object.keys(row.perStore || {}).forEach((k) => stores.add(k));
    });
    return Array.from(stores).map((id) => ({
      id,
      name: mapStoreKeyToName(id),
    }));
  }, [formattedData]);

  // ---------------- filtered data ----------------
  const filteredData = useMemo(() => {
    // helper for default empty grouped for a store
    const emptyGrouped = () => ({
      "Net Sales": 0,
      "Payment Type": {},
      "Order Discount": 0,
      "Item Discount": 0,
      Tips: 0,
    });

    return formattedData
      .filter((row) => row.addedDate !== "N/A")
      .map((row) => {
        // if a store filter is active, replace grouped with perStore[storeFilter]
        if (storeFilter) {
          const per =
            row.perStore && row.perStore[storeFilter]
              ? row.perStore[storeFilter]
              : emptyGrouped();
          return { ...row, grouped: per };
        }
        return row;
      })
      .filter((row) => {
        if (!row.addedDateTs) return false;

        const fromTs = dateFrom
          ? new Date(dateFrom + "T00:00:00").getTime()
          : null;

        const toTs = dateTo ? new Date(dateTo + "T23:59:59").getTime() : null;

        if (fromTs && row.addedDateTs < fromTs) return false;
        if (toTs && row.addedDateTs > toTs) return false;

        return true;
      });
  }, [formattedData, dateFrom, dateTo, storeFilter]);

  // ---------------- calculate totals ----------------
  const totals = useMemo(() => {
    let totalNetSales = 0;
    let totalTax = 0;
    let totalOrderDiscount = 0;
    let totalItemDiscount = 0;
    let totalTips = 0;
    filteredData.forEach((row) => {
      totalNetSales += row.grouped["Net Sales"] || 0;
      totalTax += row.grouped["Tax"] || 0;
      totalOrderDiscount += row.grouped["Order Discount"] || 0;
      totalItemDiscount += row.grouped["Item Discount"] || 0;
      totalTips += row.grouped["Tips"] || 0;
    });
    return {
      totalNetSales,
      totalTax,
      totalOrderDiscount,
      totalItemDiscount,
      totalTips,
    };
  }, [filteredData]);

  // ---------------- payment type data ----------------
  const paymentTypeData = useMemo(() => {
    const rows = [];
    filteredData.forEach((row) => {
      const pt = row.grouped["Payment Type"];
      if (pt) {
        Object.entries(pt).forEach(([name, value]) => {
          rows.push({
            addedDate: row.addedDate,
            paymentName: name,
            value: value,
          });
        });
      }
    });
    return rows;
  }, [filteredData]);

  // ---------------- filtered payment type data ----------------
  const filteredPaymentTypeData = useMemo(() => {
    return paymentTypeData.filter((row) => {
      const rowDate = new Date(row.addedDate);

      const fromDate = dateFromPT ? new Date(dateFromPT + "T00:00:00") : null;
      const toDate = dateToPT ? new Date(dateToPT + "T23:59:59") : null;

      if (fromDate && rowDate < fromDate) return false;
      if (toDate && rowDate > toDate) return false;

      if (paymentNameFilter && row.paymentName !== paymentNameFilter)
        return false;

      return true;
    });
  }, [paymentTypeData, dateFromPT, dateToPT, paymentNameFilter]);

  // ---------------- payment type totals ----------------
  const totalsPT = useMemo(() => {
    const totals = {};
    filteredPaymentTypeData.forEach((row) => {
      if (!totals[row.paymentName]) totals[row.paymentName] = 0;
      totals[row.paymentName] += row.value;
    });
    return totals;
  }, [filteredPaymentTypeData]);

  const columns = [
    { header: "Added Date", key: "addedDate" },
    { header: "Net Sales (AED)", key: "Net Sales" },
    { header: "Tax (5%) (AED)", key: "Tax" },
    { header: "Payment Type", key: "Payment Type" },
    { header: "Order Discount (AED)", key: "Order Discount" },
    { header: "Item Discount (AED)", key: "Item Discount" },
    { header: "Tips (AED)", key: "Tips" },
  ];

  const pageRows = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const pageRowsPT = filteredPaymentTypeData.slice(
    pagePT * rowsPerPagePT,
    (pagePT + 1) * rowsPerPagePT
  );
  const formatMoneyFromCents = (cents) => {
    // cents is integer (e.g. 4502 => 45.02 AED)
    if (cents == null) return "0,00";
    return (cents / 100).toFixed(2).replace(".", ",");
  };

  const applyTaxToCents = (cents, taxPercent = 5) => {
    // returns integer cents rounded
    return Math.round(cents * (1 + taxPercent / 100));
  };

  const paymentTypeString = (paymentObj = {}, applyTax = false) => {
    return Object.entries(paymentObj)
      .map(([name, cents]) => {
        const value = applyTax ? applyTaxToCents(cents) : cents;
        return `${formatMoneyFromCents(value)} (${name})`;
      })
      .join(", ");
  };

  // ---------------- export to excel ----------------
  // ---------- Replace the current exportToExcel with this ----------
  // at the top

  // --- Replace the entire exportToExcel with this ---
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Business Summary");

    // Build list of payment types (unique)
    const allPaymentTypes = Array.from(
      new Set(
        filteredData.flatMap((r) =>
          Object.keys(r.grouped["Payment Type"] || {})
        )
      )
    );

    // Subheaders
    const netSubHeaders = [
      "Added Date",
      "Net Sales (AED)",
      ...allPaymentTypes.map((p) => `${p} (AED)`),
      "Order Discount (AED)",
      "Item Discount (AED)",
      "Tips (AED)",
    ];

    const grossSubHeaders = [
      "Added Date",
      "Gross Sales (AED)",
      "VAT (AED)",
      ...allPaymentTypes.map((p) => `${p} (AED)`),
      "Order Discount (AED)",
      "Item Discount (AED)",
      "Tips (AED)",
    ];

    const netColsCount = netSubHeaders.length;
    const grossColsCount = grossSubHeaders.length;
    const totalCols = netColsCount + grossColsCount;

    // Row 1: Main merged headers
    const headerRow1 = Array(totalCols).fill("");
    headerRow1[0] = "Net Sales";
    headerRow1[netColsCount] = "Gross Sales (+5%)";
    ws.addRow(headerRow1);

    ws.mergeCells(1, 1, 1, netColsCount);
    ws.mergeCells(1, netColsCount + 1, 1, totalCols);

    const r1 = ws.getRow(1);
    r1.height = 30;
    r1.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4F81BD" },
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true, size: 16 };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    // Row 2: Subheaders
    ws.addRow([...netSubHeaders, ...grossSubHeaders]);
    const r2 = ws.getRow(2);
    r2.height = 22;
    r2.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "DDEBF7" },
      };
      cell.font = { bold: true, size: 12 };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
    });

    // Data rows
    filteredData.forEach((row) => {
      const netSales = row.grouped["Net Sales"] || 0;
      const vat = Math.round(netSales * 0.05);
      const grossSales = netSales + vat;

      const netOrderDiscount = row.grouped["Order Discount"] || 0;
      const netItemDiscount = row.grouped["Item Discount"] || 0;
      const netTips = row.grouped["Tips"] || 0;

      const netPtValues = allPaymentTypes.map(
        (pt) => (row.grouped["Payment Type"]?.[pt] || 0) / 100
      );
      const grossPtValues = netPtValues.slice();

      const dataRow = [
        row.addedDate,
        netSales / 100,
        ...netPtValues,
        netOrderDiscount / 100,
        netItemDiscount / 100,
        netTips / 100,
        row.addedDate,
        grossSales / 100,
        vat / 100,
        ...grossPtValues,
        netOrderDiscount / 100,
        netItemDiscount / 100,
        netTips / 100,
      ];

      const added = ws.addRow(dataRow);

      // Apply number format to numeric cells
      added.eachCell((cell, colNumber) => {
        // Skip Added Date columns
        if (
          colNumber !== 1 &&
          colNumber !== netColsCount + 1 &&
          typeof cell.value === "number"
        ) {
          cell.numFmt = "#,##0.00";
        }
      });
    });

    // Empty row before totals
    ws.addRow([]);

    // Totals
    const paymentTypeTotalsNet = allPaymentTypes.map((pt) =>
      filteredData.reduce(
        (s, r) => s + (r.grouped["Payment Type"]?.[pt] || 0),
        0
      )
    );

    const totalNetSales = totals.totalNetSales || 0;
    const totalVat = Math.round(totalNetSales * 0.05);
    const totalGrossSales = totalNetSales + totalVat;
    const totalOrderDiscount = totals.totalOrderDiscount || 0;
    const totalItemDiscount = totals.totalItemDiscount || 0;
    const totalTips = totals.totalTips || 0;

    const totalsRowValues = [
      "Total",
      totalNetSales / 100,
      ...paymentTypeTotalsNet.map((c) => c / 100),
      totalOrderDiscount / 100,
      totalItemDiscount / 100,
      totalTips / 100,
      "Total Gross",
      totalGrossSales / 100,
      totalVat / 100,
      ...paymentTypeTotalsNet.map((c) => c / 100),
      totalOrderDiscount / 100,
      totalItemDiscount / 100,
      totalTips / 100,
    ];

    const totalsRow = ws.addRow(totalsRowValues);
    totalsRow.eachCell((cell, colNumber) => {
      if (typeof cell.value === "number") cell.numFmt = "#,##0.00";
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F2F2F2" },
      };
    });

    // Column widths
    const leftWidths = [14, 15, ...allPaymentTypes.map(() => 16), 18, 18, 14];
    const rightWidths = [
      14,
      15,
      12,
      ...allPaymentTypes.map(() => 16),
      18,
      18,
      14,
    ];
    const finalWidths = [...leftWidths, ...rightWidths];
    finalWidths.forEach((w, i) => (ws.getColumn(i + 1).width = w));

    // Download file
    const buf = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buf]), "business_summary.xlsx");
  };

  if (loading)
    return (
      <Root>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={260}
        >
          <CircularProgress />
        </Box>
      </Root>
    );

  if (error)
    return (
      <Root>
        <Box>
          <Typography color="error">Error: {error}</Typography>
        </Box>
      </Root>
    );

  if (!formattedData.length)
    return (
      <Root>
        <Box>
          <Typography>No business summary data available.</Typography>
        </Box>
      </Root>
    );

  return (
    <Root>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <div>
          <Title>Business Summary</Title>
          <Typography variant="body2" color="text.secondary">
            Overview of business data fetched from the API with detailed fields.
          </Typography>
        </div>
      </Box>

      <GlassPaper>
        <Typography variant="h6" gutterBottom>
          Business Total
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Typography variant="body1" fontWeight="bold" color="info.main">
              Net Sales:{" "}
              {(totals.totalNetSales / 100).toFixed(2).replace(".", ",")} AED
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body1" fontWeight="bold" color="warning.main">
              Order Discount:{" "}
              {(totals.totalOrderDiscount / 100).toFixed(2).replace(".", ",")}{" "}
              AED
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body1" fontWeight="bold" color="success.main">
              Item Discount:{" "}
              {(totals.totalItemDiscount / 100).toFixed(2).replace(".", ",")}{" "}
              AED
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body1" fontWeight="bold" color="success.main">
              Tips: {(totals.totalTips / 100).toFixed(2).replace(".", ",")} AED
            </Typography>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassPaper>
        <Typography variant="h6" gutterBottom>
          Advanced Search & Export
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Date From"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Date To"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Min Net Sales"
              type="number"
              value={netSalesMin}
              onChange={(e) => setNetSalesMin(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Max Net Sales"
              type="number"
              value={netSalesMax}
              onChange={(e) => setNetSalesMax(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl style={{ width: 160 }} fullWidth size="small">
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={paymentTypeFilter}
                onChange={(e) => setPaymentTypeFilter(e.target.value)}
                label="Payment Type"
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {uniquePaymentTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl style={{ width: 160 }} fullWidth size="small">
              <InputLabel>Store</InputLabel>
              <Select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                label="Store"
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {uniqueStores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={exportToExcel}
              fullWidth
            >
              Export to Excel
            </Button>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassPaper>
        <TableContainer
          component={Paper}
          sx={{ animation: `${floatIn} 400ms ease both` }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.key}>{col.header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: "rgba(0,0,0,0.03)",
                    },
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.08)" },
                  }}
                >
                  {columns.map((col) => {
                    const grouped = row.grouped && row.grouped[col.key];
                    if (grouped) {
                      return (
                        <TableCell key={col.key} sx={{ verticalAlign: "top" }}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {col.key === "Payment Type" ? (
                              Object.entries(grouped).map(([name, sum], i) => (
                                <Tooltip
                                  key={i}
                                  style={{ marginTop: "5px" }}
                                  title={`Sum: ${(sum / 100)
                                    .toFixed(2)
                                    .replace(".", ",")}`}
                                >
                                  <Chip
                                    label={`${(sum / 100)
                                      .toFixed(2)
                                      .replace(".", ",")} (${name})`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ marginBottom: 1 }}
                                  />
                                </Tooltip>
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={
                                  col.key === "Net Sales" || col.key === "Tax"
                                    ? "info.main"
                                    : col.key === "Order Discount"
                                    ? "warning.main"
                                    : "success.main"
                                }
                              >
                                {(grouped / 100).toFixed(2).replace(".", ",")}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      );
                    } else {
                      return (
                        <TableCell key={col.key}>{row[col.key]}</TableCell>
                      );
                    }
                  })}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow sx={{ backgroundColor: "rgba(0,0,0,0.05)" }}>
                <TableCell colSpan={1} sx={{ fontWeight: "bold" }}>
                  Total
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "info.main" }}>
                  {(totals.totalNetSales / 100).toFixed(2).replace(".", ",") || "-"}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "info.main" }}>
                  {(totals.totalTax / 100).toFixed(2).replace(".", ",")}
                </TableCell>
                <TableCell></TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "warning.main" }}>
                  {(totals.totalOrderDiscount / 100)
                    .toFixed(2)
                    .replace(".", ",")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "success.main" }}>
                  {(totals.totalItemDiscount / 100)
                    .toFixed(2)
                    .replace(".", ",")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "success.main" }}>
                  {(totals.totalTips / 100).toFixed(2).replace(".", ",")}
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: "rgba(0,0,0,0.05)" }}>
                <TableCell colSpan={1} sx={{ fontWeight: "bold" }}>
                  Total with taxes (5%)
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "info.main" }}>
                  {((totals.totalNetSales / 100) * 1.05)
                    .toFixed(3)
                    .replace(".", ",")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "info.main" }}>
                  {(totals.totalTax / 100).toFixed(2).replace(".", ",")}
                </TableCell>
                <TableCell></TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "warning.main" }}>
                  {(totals.totalOrderDiscount / 100)
                    .toFixed(2)
                    .replace(".", ",")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "success.main" }}>
                  {(totals.totalItemDiscount / 100)
                    .toFixed(2)
                    .replace(".", ",")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "success.main" }}>
                  {(totals.totalTips / 100).toFixed(2).replace(".", ",")}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </Box>
      </GlassPaper>

      <GlassPaper>
        <Typography variant="h6" gutterBottom>
          Payment Types Details
        </Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date From"
              type="date"
              value={dateFromPT}
              onChange={(e) => setDateFromPT(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date To"
              type="date"
              value={dateToPT}
              onChange={(e) => setDateToPT(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl style={{ width: 200 }} fullWidth size="small">
              <InputLabel>Payment Name</InputLabel>
              <Select
                value={paymentNameFilter}
                onChange={(e) => setPaymentNameFilter(e.target.value)}
                label="Payment Name"
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {uniquePaymentTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <TableContainer
          component={Paper}
          sx={{ animation: `${floatIn} 400ms ease both` }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Added Date</TableCell>
                <TableCell>Payment Name</TableCell>
                <TableCell>Value (AED)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRowsPT.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: "rgba(0,0,0,0.03)",
                    },
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.08)" },
                  }}
                >
                  <TableCell>{row.addedDate ?? "-"}</TableCell>
                  <TableCell>{row.paymentName || "-"}</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "success.main" }}>
                    {(row.value / 100).toFixed(2).replace(".", ",") || "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            <TableFooter>
              {Object.entries(totalsPT).map(([name, total]) => (
                <TableRow
                  key={name}
                  sx={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                >
                  <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                    Total {name}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "success.main" }}>
                    {(total / 100).toFixed(2).replace(".", ",")}
                  </TableCell>
                </TableRow>
              ))}
            </TableFooter>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <TablePagination
            component="div"
            count={filteredPaymentTypeData.length}
            page={pagePT}
            rowsPerPage={rowsPerPagePT}
            onPageChange={(e, newPage) => setPagePT(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPagePT(parseInt(e.target.value, 10));
              setPagePT(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </Box>
      </GlassPaper>
    </Root>
  );
};

// ---------------- wrapper ----------------
const BusinessSummaryPage = () => (
  <BusinessSummaryProvider>
    <BusinessSummary />
  </BusinessSummaryProvider>
);

export default BusinessSummaryPage;
